import React from "react";
import {
  FaCode,
  FaMobile,
  FaLaptopCode,
  FaDatabase,
  FaCloud,
  FaShieldAlt,
  FaChartLine,
  FaRobot,
  FaServer,
  FaCogs,
  FaPalette,
  FaHeadset
} from "react-icons/fa";
import { FiChevronRight } from "react-icons/fi";

const Services = () => {
  const services = [
    {
      icon: <FaCode />,
      title: "Web Development",
      desc: "Modern, responsive, and high-performance websites and web applications using React, Next.js, and modern frameworks.",
      keywords: ["React", "Next.js", "JavaScript", "TypeScript", "Responsive Design"]
    },
    {
      icon: <FaMobile />,
      title: "Mobile App Development",
      desc: "Native & cross-platform mobile applications for iOS & Android with smooth UX and enterprise-grade security.",
      keywords: ["React Native", "Flutter", "iOS", "Android", "Mobile UX"]
    },
    {
      icon: <FaLaptopCode />,
      title: "Custom Software Solutions",
      desc: "Tailored enterprise software to automate workflows, enhance productivity, and drive business transformation.",
      keywords: ["Enterprise Software", "Automation", "Workflow", "SaaS"]
    },
    {
      icon: <FaDatabase />,
      title: "Database & Backend Development",
      desc: "Scalable database architecture, API development, and microservices using modern backend technologies.",
      keywords: ["Node.js", "Python", "MongoDB", "PostgreSQL", "REST APIs"]
    },
    {
      icon: <FaCloud />,
      title: "Cloud & DevOps Services",
      desc: "Cloud migration, CI/CD pipelines, containerization, and infrastructure automation for scalable deployments.",
      keywords: ["AWS", "Azure", "Docker", "Kubernetes", "CI/CD"]
    },
    {
      icon: <FaShieldAlt />,
      title: "Cybersecurity Solutions",
      desc: "Comprehensive security audits, vulnerability assessments, and implementation of enterprise security protocols.",
      keywords: ["Security", "Compliance", "Audit", "Data Protection"]
    },
    {
      icon: <FaChartLine />,
      title: "Digital Transformation",
      desc: "Strategic consulting and implementation services to modernize legacy systems and embrace digital innovation.",
      keywords: ["Digital Strategy", "Modernization", "Consulting"]
    },
    {
      icon: <FaRobot />,
      title: "AI & Machine Learning",
      desc: "Intelligent solutions leveraging AI/ML for predictive analytics, automation, and data-driven insights.",
      keywords: ["AI", "Machine Learning", "Data Science", "Automation"]
    },
    {
      icon: <FaServer />,
      title: "IT Infrastructure",
      desc: "End-to-end IT infrastructure setup, management, and optimization for seamless business operations.",
      keywords: ["Infrastructure", "Networking", "System Administration"]
    },
    {
      icon: <FaCogs />,
      title: "Process Automation",
      desc: "Workflow automation solutions to streamline operations, reduce costs, and improve efficiency.",
      keywords: ["RPA", "Automation", "Efficiency", "Workflow"]
    },
    {
      icon: <FaPalette />,
      title: "UI/UX Design",
      desc: "User-centric design solutions focusing on intuitive interfaces, engaging experiences, and brand consistency.",
      keywords: ["UI Design", "UX Research", "Prototyping", "User Testing"]
    },
    {
      icon: <FaHeadset />,
      title: "24/7 Technical Support",
      desc: "Round-the-clock monitoring, maintenance, and technical support to ensure uninterrupted business operations.",
      keywords: ["Support", "Maintenance", "Monitoring", "Helpdesk"]
    }
  ];

  return (
    <section 
      id="services" 
      className="cs_services_wrapper"
      itemScope 
      itemType="https://schema.org/Service"
    >
      {/* SEO Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "IT Services by Codriva Tech",
            "description": "Comprehensive IT services including web development, mobile app development, cloud solutions, cybersecurity, and digital transformation.",
            "provider": {
              "@type": "Organization",
              "name": "Codriva Tech",
              "url": "https://codrivatech.com"
            },
            "areaServed": "Worldwide",
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "IT Services",
              "itemListElement": services.map((service, index) => ({
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": service.title,
                  "description": service.desc
                }
              }))
            }
          })
        }}
      />

      <div className="cs_container">
        <div className="cs_section_header">
          <div className="cs_section_badge">
            <span className="cs_badge_dot"></span>
            <span className="cs_badge_text">Our Expertise</span>
          </div>
          
          <h1 className="cs_section_title" itemProp="headline">
            <span className="cs_title_line">Comprehensive</span>
            <span className="cs_title_line">
              <span className="cs_gradient_text">IT Services</span> For
            </span>
            <span className="cs_title_line">Digital Growth</span>
          </h1>

          <p className="cs_section_description" itemProp="description">
            End-to-end IT services designed to help your business grow, scale,
            and stay ahead in the digital world. We deliver innovative solutions
            that drive measurable results and sustainable growth.
          </p>

          <div className="cs_services_stats">
            <div className="cs_stat_item">
              <span className="cs_stat_number">12+</span>
              <span className="cs_stat_label">Service Domains</span>
            </div>
            <div className="cs_stat_divider"></div>
            <div className="cs_stat_item">
              <span className="cs_stat_number">24/7</span>
              <span className="cs_stat_label">Support Available</span>
            </div>
          </div>
        </div>

        <div className="cs_services_grid">
          {services.map((service, index) => (
            <div
              key={index}
              className="cs_service_card"
              itemScope
              itemType="https://schema.org/Service"
              itemProp="hasOfferCatalog"
            >
              <div className="cs_card_content">
                <div className="cs_card_icon_wrapper">
                  <div className="cs_icon_background"></div>
                  <div className="cs_service_icon" itemProp="icon">
                    {service.icon}
                  </div>
                </div>

                <h3 className="cs_service_title" itemProp="name">
                  {service.title}
                </h3>

                <p className="cs_service_description" itemProp="description">
                  {service.desc}
                </p>

                <div className="cs_service_keywords">
                  {service.keywords?.map((keyword, idx) => (
                    <span key={idx} className="cs_keyword_tag">
                      {keyword}
                    </span>
                  ))}
                </div>

                <a 
                  href="#contact" 
                  className="cs_service_cta"
                  aria-label={`Learn more about ${service.title}`}
                >
                  <span>Learn More</span>
                  <FiChevronRight className="cs_cta_icon" />
                </a>
              </div>
              <div className="cs_card_hover_effect"></div>
            </div>
          ))}
        </div>

        <div className="cs_services_cta_section">
          <p className="cs_cta_text">
            Can't find what you're looking for? We offer custom solutions tailored to your specific needs.
          </p>
          <a href="#contact" className="cs_cta_button">
            <span>Discuss Your Project</span>
            <FiChevronRight className="cs_button_icon" />
          </a>
        </div>
      </div>

      <style jsx>{`
        .cs_services_wrapper {
          padding: 160px 0;
          background: linear-gradient(135deg, 
            #020617 0%,
            #0f172a 50%,
            #1e293b 100%);
          color: #f8fafc;
          position: relative;
          overflow: hidden;
          width: 100%;
        }

        .cs_services_wrapper::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(90deg, 
            transparent, 
            rgba(34, 211, 238, 0.3), 
            transparent);
        }

        .cs_container {
          max-width: 1400px;
          margin: 0 auto;
          padding: 0 32px;
          position: relative;
          z-index: 2;
        }

        /* Section Header */
        .cs_section_header {
          text-align: center;
          max-width: 900px;
          margin: 0 auto 80px;
        }

        .cs_section_badge {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: rgba(34, 211, 238, 0.1);
          padding: 10px 24px;
          border-radius: 50px;
          margin-bottom: 32px;
        }

        .cs_badge_dot {
          width: 8px;
          height: 8px;
          background: #22d3ee;
          border-radius: 50%;
        }

        .cs_badge_text {
          font-size: 0.9rem;
          font-weight: 600;
          color: #94a3b8;
          letter-spacing: 1px;
        }

        /* Section Title */
        .cs_section_title {
          font-size: 4rem;
          font-weight: 800;
          line-height: 1.1;
          margin-bottom: 24px;
        }

        .cs_title_line {
          display: block;
        }

        .cs_gradient_text {
          background: linear-gradient(135deg, #22d3ee 0%, #3b82f6 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .cs_section_description {
          font-size: 1.25rem;
          line-height: 1.7;
          color: #cbd5e1;
          max-width: 700px;
          margin: 0 auto 48px;
        }

        /* Stats */
        .cs_services_stats {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 48px;
        }

        .cs_stat_item {
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .cs_stat_number {
          font-size: 2.5rem;
          font-weight: 700;
          background: linear-gradient(135deg, #22d3ee, #3b82f6);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          margin-bottom: 4px;
        }

        .cs_stat_label {
          font-size: 0.9rem;
          color: #94a3b8;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        .cs_stat_divider {
          width: 1px;
          height: 40px;
          background: rgba(148, 163, 184, 0.2);
        }

        /* Services Grid */
        .cs_services_grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 30px;
          margin-bottom: 80px;
        }

        /* Service Card */
        .cs_service_card {
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid rgba(255, 255, 255, 0.05);
          border-radius: 20px;
          padding: 40px 32px;
          position: relative;
          overflow: hidden;
          backdrop-filter: blur(10px);
          transition: all 0.3s ease;
        }

        .cs_service_card:hover {
          transform: translateY(-5px);
          border-color: rgba(34, 211, 238, 0.3);
          box-shadow: 
            0 20px 40px rgba(0, 0, 0, 0.3),
            0 0 0 1px rgba(34, 211, 238, 0.1);
        }

        .cs_card_content {
          position: relative;
          z-index: 2;
        }

        .cs_card_icon_wrapper {
          position: relative;
          width: 80px;
          height: 80px;
          margin-bottom: 28px;
        }

        .cs_icon_background {
          position: absolute;
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, 
            rgba(34, 211, 238, 0.1), 
            rgba(59, 130, 246, 0.1));
          border-radius: 20px;
          transform: rotate(45deg);
          transition: transform 0.3s ease;
        }

        .cs_service_card:hover .cs_icon_background {
          transform: rotate(135deg);
        }

        .cs_service_icon {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          font-size: 2.5rem;
          color: #22d3ee;
          transition: color 0.3s ease;
        }

        .cs_service_card:hover .cs_service_icon {
          color: #3b82f6;
        }

        .cs_service_title {
          font-size: 1.5rem;
          font-weight: 700;
          margin-bottom: 16px;
          color: #f8fafc;
        }

        .cs_service_description {
          font-size: 1rem;
          line-height: 1.7;
          color: #94a3b8;
          margin-bottom: 24px;
          min-height: 80px;
        }

        .cs_service_keywords {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-bottom: 28px;
        }

        .cs_keyword_tag {
          font-size: 0.75rem;
          padding: 4px 12px;
          background: rgba(34, 211, 238, 0.1);
          color: #22d3ee;
          border-radius: 50px;
          border: 1px solid rgba(34, 211, 238, 0.2);
        }

        .cs_service_cta {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          color: #22d3ee;
          text-decoration: none;
          font-weight: 600;
          font-size: 0.95rem;
          transition: all 0.3s ease;
        }

        .cs_service_cta:hover {
          gap: 12px;
        }

        .cs_cta_icon {
          font-size: 1.1rem;
          transition: transform 0.3s ease;
        }

        .cs_service_cta:hover .cs_cta_icon {
          transform: translateX(4px);
        }

        .cs_card_hover_effect {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, 
            rgba(34, 211, 238, 0.05), 
            transparent 30%);
          opacity: 0;
          transition: opacity 0.3s ease;
          pointer-events: none;
        }

        .cs_service_card:hover .cs_card_hover_effect {
          opacity: 1;
        }

        /* CTA Section */
        .cs_services_cta_section {
          text-align: center;
          padding: 60px;
          background: linear-gradient(135deg, 
            rgba(34, 211, 238, 0.05), 
            rgba(59, 130, 246, 0.05));
          border-radius: 24px;
          border: 1px solid rgba(34, 211, 238, 0.1);
          backdrop-filter: blur(10px);
        }

        .cs_cta_text {
          font-size: 1.5rem;
          font-weight: 600;
          margin-bottom: 32px;
          color: #f8fafc;
        }

        .cs_cta_button {
          display: inline-flex;
          align-items: center;
          gap: 12px;
          padding: 18px 40px;
          background: linear-gradient(135deg, #22d3ee, #3b82f6);
          color: #020617;
          border-radius: 12px;
          text-decoration: none;
          font-weight: 700;
          font-size: 1.1rem;
          transition: all 0.3s ease;
          box-shadow: 0 10px 30px rgba(34, 211, 238, 0.3);
        }

        .cs_cta_button:hover {
          transform: translateY(-3px);
          box-shadow: 0 15px 40px rgba(34, 211, 238, 0.4);
          gap: 16px;
        }

        .cs_button_icon {
          font-size: 1.2rem;
          transition: transform 0.3s ease;
        }

        .cs_cta_button:hover .cs_button_icon {
          transform: translateX(4px);
        }

        /* ========== RESPONSIVE STYLES ========== */
        
        /* Desktop Large (1200px - 1400px) */
        @media screen and (max-width: 1400px) {
          .cs_container {
            max-width: 1140px;
          }
          
          .cs_services_grid {
            grid-template-columns: repeat(3, 1fr);
            gap: 25px;
          }
        }

        /* Desktop Medium (992px - 1199px) */
        @media screen and (max-width: 1199px) {
          .cs_services_wrapper {
            padding: 120px 0;
          }

          .cs_container {
            max-width: 960px;
            padding: 0 24px;
          }

          .cs_section_title {
            font-size: 3.5rem;
          }

          .cs_services_grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 25px;
          }

          .cs_service_card {
            padding: 35px 28px;
          }
        }

        /* Tablet (768px - 991px) */
        @media screen and (max-width: 991px) {
          .cs_services_wrapper {
            padding: 100px 0;
          }

          .cs_container {
            max-width: 720px;
            padding: 0 20px;
          }

          .cs_section_header {
            margin-bottom: 60px;
          }

          .cs_section_title {
            font-size: 3rem;
          }

          .cs_section_description {
            font-size: 1.1rem;
            margin-bottom: 40px;
          }

          .cs_services_stats {
            gap: 30px;
          }

          .cs_stat_number {
            font-size: 2rem;
          }

          .cs_services_grid {
            gap: 20px;
          }

          .cs_service_card {
            padding: 30px 25px;
          }

          .cs_card_icon_wrapper {
            width: 70px;
            height: 70px;
            margin-bottom: 24px;
          }

          .cs_service_icon {
            font-size: 2.2rem;
          }

          .cs_service_title {
            font-size: 1.3rem;
          }

          .cs_service_description {
            font-size: 0.95rem;
            min-height: 90px;
          }

          .cs_services_cta_section {
            padding: 50px 40px;
          }

          .cs_cta_text {
            font-size: 1.3rem;
          }
        }

        /* Mobile Landscape (576px - 767px) */
        @media screen and (max-width: 767px) {
          .cs_services_wrapper {
            padding: 80px 0;
          }

          .cs_container {
            max-width: 540px;
            padding: 0 16px;
          }

          .cs_section_badge {
            padding: 8px 20px;
            margin-bottom: 24px;
          }

          .cs_badge_text {
            font-size: 0.85rem;
          }

          .cs_section_title {
            font-size: 2.5rem;
            margin-bottom: 20px;
          }

          .cs_section_description {
            font-size: 1rem;
            margin-bottom: 32px;
            line-height: 1.6;
          }

          .cs_services_stats {
            flex-direction: column;
            gap: 16px;
          }

          .cs_stat_divider {
            display: none;
          }

          .cs_stat_item {
            flex-direction: row;
            gap: 12px;
          }

          .cs_stat_number {
            font-size: 1.8rem;
            margin-bottom: 0;
          }

          .cs_stat_label {
            font-size: 0.85rem;
          }

          .cs_services_grid {
            grid-template-columns: 1fr;
            gap: 20px;
            margin-bottom: 60px;
          }

          .cs_service_card {
            padding: 30px 25px;
          }

          .cs_service_description {
            min-height: auto;
            margin-bottom: 20px;
          }

          .cs_services_cta_section {
            padding: 40px 30px;
          }

          .cs_cta_text {
            font-size: 1.2rem;
            margin-bottom: 28px;
          }

          .cs_cta_button {
            padding: 16px 32px;
            font-size: 1rem;
          }
        }

        /* Mobile Portrait (320px - 575px) */
        @media screen and (max-width: 575px) {
          .cs_services_wrapper {
            padding: 60px 0;
          }

          .cs_container {
            padding: 0 20px;
          }

          .cs_section_header {
            margin-bottom: 40px;
          }

          .cs_section_badge {
            padding: 6px 16px;
            margin-bottom: 20px;
          }

          .cs_badge_dot {
            width: 6px;
            height: 6px;
          }

          .cs_badge_text {
            font-size: 0.8rem;
          }

          .cs_section_title {
            font-size: 2rem;
            margin-bottom: 16px;
          }

          .cs_section_description {
            font-size: 0.95rem;
            margin-bottom: 28px;
          }

          .cs_stat_number {
            font-size: 1.6rem;
          }

          .cs_stat_label {
            font-size: 0.8rem;
          }

          .cs_service_card {
            padding: 28px 22px;
          }

          .cs_card_icon_wrapper {
            width: 60px;
            height: 60px;
            margin-bottom: 20px;
          }

          .cs_service_icon {
            font-size: 1.8rem;
          }

          .cs_service_title {
            font-size: 1.2rem;
            margin-bottom: 12px;
          }

          .cs_service_description {
            font-size: 0.9rem;
            line-height: 1.6;
          }

          .cs_service_keywords {
            gap: 6px;
            margin-bottom: 24px;
          }

          .cs_keyword_tag {
            font-size: 0.7rem;
            padding: 3px 10px;
          }

          .cs_service_cta {
            font-size: 0.9rem;
          }

          .cs_services_cta_section {
            padding: 30px 20px;
          }

          .cs_cta_text {
            font-size: 1.1rem;
            margin-bottom: 24px;
          }

          .cs_cta_button {
            padding: 14px 28px;
            font-size: 0.95rem;
            gap: 8px;
          }

          .cs_button_icon {
            font-size: 1.1rem;
          }
        }

        /* Small Mobile (320px) */
        @media screen and (max-width: 360px) {
          .cs_section_title {
            font-size: 1.8rem;
          }

          .cs_service_card {
            padding: 24px 20px;
          }

          .cs_card_icon_wrapper {
            width: 50px;
            height: 50px;
            margin-bottom: 18px;
          }

          .cs_service_icon {
            font-size: 1.5rem;
          }

          .cs_service_title {
            font-size: 1.1rem;
          }

          .cs_service_description {
            font-size: 0.85rem;
          }

          .cs_keyword_tag {
            font-size: 0.65rem;
            padding: 2px 8px;
          }

          .cs_cta_text {
            font-size: 1rem;
          }

          .cs_cta_button {
            padding: 12px 24px;
            font-size: 0.9rem;
            width: 100%;
            justify-content: center;
          }
        }

        /* Print Styles */
        @media print {
          .cs_services_wrapper {
            background: none;
            color: #000;
            padding: 40px 0;
          }

          .cs_service_card,
          .cs_services_cta_section {
            border: 1px solid #ddd;
            box-shadow: none;
            background: none;
          }

          .cs_cta_button,
          .cs_service_cta {
            display: none;
          }
        }
      `}</style>
    </section>
  );
};

export default Services;